import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Eye,
  Users,
  Clock,
  MousePointerClick,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  ExternalLink,
  RefreshCw,
  Zap,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import api from "../api/client";

const PERIODS = [
  { label: "7 Days", value: "7d" },
  { label: "30 Days", value: "30d" },
  { label: "90 Days", value: "90d" },
];

const SOURCE_COLORS = {
  organic: "#22c55e",
  direct: "#3b82f6",
  social: "#a855f7",
  referral: "#f59e0b",
  email: "#ef4444",
  paid: "#ec4899",
};

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

function TrendBadge({ value }) {
  if (value === 0) return <span className="text-xs text-gray-500 font-mono">0%</span>;
  if (value > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-green-400 font-mono">
        <ArrowUp size={12} />+{value}%
      </span>
    );
  return (
    <span className="flex items-center gap-0.5 text-xs text-red-400 font-mono">
      <ArrowDown size={12} />{value}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, trend, color = "text-copper", onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-dark-300 border border-dark-50 rounded-xl p-5 transition-all ${
        onClick
          ? "cursor-pointer hover:border-copper/40 hover:shadow-[0_0_12px_rgba(184,115,51,0.15)]"
          : ""
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-dark-50/50 rounded-lg">
          <Icon size={18} className={color} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
        {trend !== undefined && <TrendBadge value={trend} />}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm">
      <div className="text-gray-400 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-white font-mono">{p.value.toLocaleString()}</span>
          <span className="text-gray-500">{p.dataKey}</span>
        </div>
      ))}
    </div>
  );
}

export default function Analytics({ selectedSite }) {
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [organicGsc, setOrganicGsc] = useState(0);
  const [locations, setLocations] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageDetail, setPageDetail] = useState(null);
  const [showPageDetail, setShowPageDetail] = useState(false);
  const [pageSort, setPageSort] = useState("views");
  const topPagesRef = useRef(null);

  const siteId = selectedSite?.id;

  const fetchData = useCallback(() => {
    if (!siteId) return;
    setLoading(true);

    const params = { period };

    Promise.all([
      api.get(`/websites/${siteId}/analytics/overview/`, { params }),
      api.get(`/websites/${siteId}/analytics/pages/`, { params }),
      api.get(`/websites/${siteId}/analytics/sources/`, { params }),
      api.get(`/websites/${siteId}/analytics/locations/`, { params }),
      api.get(`/websites/${siteId}/analytics/clicks/`, { params }),
      api.get(`/websites/${siteId}/analytics/realtime/`),
    ])
      .then(([ovRes, pgRes, srcRes, locRes, clkRes, rtRes]) => {
        setOverview(ovRes.data);
        setPages(pgRes.data.results || pgRes.data);
        setSources(srcRes.data.sources || srcRes.data.results || srcRes.data);
        setOrganicGsc(srcRes.data.organic_gsc_clicks || 0);
        setLocations(locRes.data.results || locRes.data);
        setClicks(clkRes.data.results || clkRes.data);
        setRealtime(rtRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh realtime every 15 seconds
  useEffect(() => {
    if (!siteId) return;
    const interval = setInterval(() => {
      api.get(`/websites/${siteId}/analytics/realtime/`)
        .then((res) => setRealtime(res.data))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [siteId]);

  const fetchPageDetail = useCallback((pageUrl) => {
    if (!siteId) return;
    setShowPageDetail(true);
    setPageDetail(null);
    api.get(`/websites/${siteId}/analytics/page-detail/`, { params: { url: pageUrl, period } })
      .then((res) => setPageDetail(res.data))
      .catch(() => setPageDetail(null));
  }, [siteId, period]);

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to view analytics
      </div>
    );
  }

  const scrollToPages = useCallback((sort) => {
    setPageSort(sort);
    setTimeout(() => {
      topPagesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const s = overview?.summary || {};
  const trend = overview?.daily_trend || [];

  // Prepare source data for pie chart
  const sourceData = sources.map((src) => ({
    name: src.source,
    value: src.count,
    color: SOURCE_COLORS[src.source] || "#6b7280",
  }));

  // Device data for bar chart
  const deviceData = (overview?.device_breakdown || []).map((d) => ({
    name: d.device_type,
    count: d.count,
  }));
  const totalDevices = deviceData.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-mono">Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Visitor traffic and engagement for {selectedSite?.domain}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Real-time badge */}
          {realtime && (
            <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
              <Zap size={14} className="text-green-400" />
              <span className="text-sm font-mono text-green-400">
                {realtime.active_visitors} live
              </span>
            </div>
          )}

          {/* Period selector */}
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p.value
                    ? "bg-copper text-white"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchData}
            className="p-2 bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-copper font-mono animate-pulse">Loading analytics...</div>
        </div>
      ) : !overview || s.pageviews === 0 ? (
        /* Empty state */
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center">
          <Eye size={48} className="text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No tracking data yet</h2>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Install the OnesuiteSEO plugin on your WordPress site and configure
            the API URL and Site Key to start tracking visitors.
          </p>
          <div className="bg-dark-50/50 rounded-lg p-4 max-w-sm mx-auto text-left text-sm text-gray-400 font-mono">
            <div>API URL: <span className="text-copper">https://app.gcmmgroup.com</span></div>
            <div>Site Key: <span className="text-copper">{selectedSite?.wp_autoseo_api_key || "Set in Settings"}</span></div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              icon={Eye}
              label="Pageviews"
              value={s.pageviews?.toLocaleString()}
              trend={s.pageviews_trend}
              onClick={() => scrollToPages("views")}
            />
            <StatCard
              icon={Users}
              label="Sessions"
              value={s.sessions?.toLocaleString()}
              trend={s.sessions_trend}
              color="text-blue-400"
              onClick={() => scrollToPages("sessions")}
            />
            <StatCard
              icon={MousePointerClick}
              label="Pages/Session"
              value={s.pages_per_session}
              color="text-purple-400"
              onClick={() => scrollToPages("views")}
            />
            <StatCard
              icon={TrendingUp}
              label="Bounce Rate"
              value={s.bounce_rate + "%"}
              color="text-amber-400"
              onClick={() => scrollToPages("bounce")}
            />
            <StatCard
              icon={Clock}
              label="Time (median · mean)"
              value={(s.median_time_on_page ?? 0) + "s · " + (s.avg_time_on_page ?? 0) + "s"}
              color="text-green-400"
              onClick={() => scrollToPages("time")}
            />
          </div>

          {/* Main Chart */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4">Traffic Over Time</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="colorPV" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#B87333" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#B87333" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorSess" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickFormatter={(v) => {
                      if (!v) return "";
                      const d = new Date(v);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="pageviews"
                    stroke="#B87333"
                    strokeWidth={2}
                    fill="url(#colorPV)"
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#colorSess)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-6 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-copper rounded" />Pageviews
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-blue-500 rounded" />Sessions
              </span>
            </div>
          </div>

          {/* 2x2 Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            <div ref={topPagesRef} className="bg-dark-300 border border-dark-50 rounded-xl p-6 scroll-mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-400">Top Pages</h3>
                <div className="flex gap-1 text-xs">
                  {[
                    { key: "views", label: "Views" },
                    { key: "sessions", label: "Sessions" },
                    { key: "time", label: "Avg Time" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setPageSort(opt.key)}
                      className={`px-2 py-1 rounded transition-colors ${
                        pageSort === opt.key
                          ? "bg-copper/20 text-copper"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(() => {
                  const topPages = [...(overview?.top_pages || [])];
                  if (pageSort === "sessions") topPages.sort((a, b) => (b.sessions || 0) - (a.sessions || 0));
                  else if (pageSort === "time") topPages.sort((a, b) => (b.avg_time || 0) - (a.avg_time || 0));
                  else if (pageSort === "bounce") topPages.sort((a, b) => (b.avg_scroll || 0) - (a.avg_scroll || 0));
                  else topPages.sort((a, b) => (b.views || 0) - (a.views || 0));

                  const sortLabel =
                    pageSort === "sessions" ? "sess" :
                    pageSort === "time" ? "s avg" :
                    pageSort === "bounce" ? "% scroll" :
                    "views";

                  return topPages.length > 0 ? topPages.map((page, i) => {
                    const sortValue =
                      pageSort === "sessions" ? page.sessions :
                      pageSort === "time" ? Math.round(page.avg_time || 0) :
                      pageSort === "bounce" ? Math.round(page.avg_scroll || 0) :
                      page.views;

                    return (
                      <button
                        key={i}
                        onClick={() => fetchPageDetail(page.page_url)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-dark-50/30 hover:bg-dark-50 hover:border-copper/30 border border-transparent transition-all text-left group cursor-pointer"
                      >
                        <span className="text-xs font-mono text-gray-600 w-5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate group-hover:text-copper transition-colors">
                            {page.page_title || page.page_url}
                          </div>
                          <div className="text-xs text-gray-600 truncate">{page.page_url}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-mono text-white">{sortValue}</div>
                          <div className="text-xs text-gray-500">{sortLabel}</div>
                        </div>
                        <ExternalLink size={12} className="text-gray-600 group-hover:text-copper" />
                      </button>
                    );
                  }) : (
                    <div className="text-sm text-gray-600 text-center py-4">No page data yet</div>
                  );
                })()}
              </div>
            </div>

            {/* Traffic Sources */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
              <h3 className="text-sm font-medium text-gray-400 mb-4">Traffic Sources</h3>
              {sourceData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <div className="w-40 h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sourceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          dataKey="value"
                          stroke="none"
                        >
                          {sourceData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {sources.map((src, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ background: SOURCE_COLORS[src.source] || "#6b7280" }}
                        />
                        <span className="text-sm text-gray-300 capitalize flex-1">{src.source}</span>
                        <span className="text-sm font-mono text-white">{src.count}</span>
                        <span className="text-xs text-gray-500 w-10 text-right">{src.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600 text-center py-8">No source data yet</div>
              )}
              {organicGsc > 0 && (
                <div className="mt-4 pt-4 border-t border-dark-50 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: SOURCE_COLORS.organic }} />
                  <span className="text-sm text-gray-300 flex-1">
                    Organic Search (GSC clicks)
                  </span>
                  <span className="text-sm font-mono text-white">{organicGsc}</span>
                </div>
              )}
              {organicGsc > 0 && (
                <p className="mt-2 text-xs text-gray-600">
                  From Google Search Console, shown separately so it is not double-counted with beacon traffic above.
                </p>
              )}
            </div>

            {/* Locations */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
              <h3 className="text-sm font-medium text-gray-400 mb-4">
                <Globe size={14} className="inline mr-1.5" />
                Top Locations
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {locations.length > 0 ? (
                  locations.map((loc, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-dark-50/20">
                      <span className="text-xs font-mono text-gray-600 w-5">{i + 1}</span>
                      <div className="flex-1">
                        <span className="text-sm text-white">
                          {loc.city}{loc.region ? `, ${loc.region}` : ""}
                        </span>
                        {loc.country && (
                          <span className="text-xs text-gray-500 ml-1">({loc.country})</span>
                        )}
                      </div>
                      <span className="text-sm font-mono text-copper">{loc.count}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-600 text-center py-4">
                    No location data available. Enable GeoIP lookup to see visitor locations.
                  </div>
                )}
              </div>
            </div>

            {/* Devices & Clicks */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
              <h3 className="text-sm font-medium text-gray-400 mb-4">Devices</h3>
              {deviceData.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {deviceData.map((d, i) => {
                    const pct = totalDevices > 0 ? Math.round(d.count / totalDevices * 100) : 0;
                    const DeviceIcon = DEVICE_ICONS[d.name] || Monitor;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="flex items-center gap-2 text-sm text-gray-300">
                            <DeviceIcon size={14} />
                            <span className="capitalize">{d.name}</span>
                          </span>
                          <span className="text-sm font-mono text-white">{d.count} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-dark-50 rounded-full h-2">
                          <div
                            className="bg-copper rounded-full h-2 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-600 text-center py-4">No device data yet</div>
              )}

              {/* Top Clicks */}
              <h3 className="text-sm font-medium text-gray-400 mb-3 mt-4">Top Clicked Elements</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {clicks.length > 0 ? (
                  clicks.slice(0, 8).map((clk, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-dark-50/20">
                      <span className="text-xs bg-dark-50 px-1.5 py-0.5 rounded font-mono text-gray-400">
                        {clk.element_tag}
                      </span>
                      <span className="text-sm text-gray-300 flex-1 truncate">
                        {clk.element_text || clk.element_href || "—"}
                      </span>
                      <span className="text-sm font-mono text-copper">{clk.count}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-600 text-center">No click data yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Top Referrers */}
          {(overview?.top_referrers || []).length > 0 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
              <h3 className="text-sm font-medium text-gray-400 mb-4">Top Referrers</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {overview.top_referrers.map((ref, i) => (
                  <div key={i} className="bg-dark-50/30 rounded-lg p-3 text-center">
                    <div className="text-sm text-white truncate">{ref.domain}</div>
                    <div className="text-lg font-mono text-copper mt-1">{ref.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Real-time Active Pages */}
          {realtime && realtime.active_visitors > 0 && (
            <div className="bg-dark-300 border border-green-500/20 rounded-xl p-6">
              <h3 className="text-sm font-medium text-green-400 mb-4 flex items-center gap-2">
                <Zap size={14} />
                Real-Time — {realtime.active_visitors} active visitor{realtime.active_visitors !== 1 ? "s" : ""}
              </h3>
              <div className="space-y-2">
                {(realtime.active_pages || []).map((pg, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-dark-50/20">
                    <div className="flex-1 text-sm text-white truncate">
                      {pg.page_title || pg.page_url}
                    </div>
                    <span className="text-sm font-mono text-green-400">{pg.visitors}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Page Detail Modal */}
      {showPageDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowPageDetail(false)}>
          <div className="bg-dark-300 border border-dark-50 rounded-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            {!pageDetail ? (
              <div className="text-center text-copper font-mono animate-pulse py-12">Loading page detail...</div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-bold">{pageDetail.page_title || "Page Detail"}</h2>
                    <p className="text-sm text-gray-500 mt-1 break-all">{pageDetail.page_url}</p>
                  </div>
                  <button onClick={() => setShowPageDetail(false)} className="text-gray-500 hover:text-white text-xl">&times;</button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-dark-50/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Views</div>
                    <div className="text-xl font-mono font-bold text-copper">{pageDetail.total_views}</div>
                  </div>
                  <div className="bg-dark-50/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Sessions</div>
                    <div className="text-xl font-mono font-bold">{pageDetail.sessions}</div>
                  </div>
                  <div className="bg-dark-50/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Avg Time</div>
                    <div className="text-xl font-mono font-bold">{pageDetail.avg_time_on_page}s</div>
                  </div>
                  <div className="bg-dark-50/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Avg Scroll</div>
                    <div className="text-xl font-mono font-bold">{pageDetail.avg_scroll_depth}%</div>
                  </div>
                </div>

                {/* Daily chart */}
                {pageDetail.daily_views?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm text-gray-400 mb-3">Views Over Time</h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={pageDetail.daily_views}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }}
                            tickFormatter={(v) => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                          <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="views" stroke="#B87333" fill="#B87333" fillOpacity={0.2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Source breakdown */}
                {pageDetail.sources?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm text-gray-400 mb-3">Traffic Sources</h3>
                    <div className="flex flex-wrap gap-2">
                      {pageDetail.sources.map((src, i) => (
                        <span key={i} className="bg-dark-50/50 rounded-lg px-3 py-1.5 text-sm">
                          <span className="capitalize text-gray-300">{src.source}</span>
                          <span className="text-copper font-mono ml-2">{src.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Click events */}
                {pageDetail.clicks?.length > 0 && (
                  <div>
                    <h3 className="text-sm text-gray-400 mb-3">Click Events</h3>
                    <div className="space-y-2">
                      {pageDetail.clicks.map((clk, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-dark-50/30">
                          <span className="text-xs bg-dark-50 px-1.5 py-0.5 rounded font-mono text-gray-400">{clk.element_tag}</span>
                          <span className="text-sm text-gray-300 flex-1 truncate">{clk.element_text || "—"}</span>
                          {clk.element_href && (
                            <span className="text-xs text-gray-500 truncate max-w-[200px]">{clk.element_href}</span>
                          )}
                          <span className="text-sm font-mono text-copper font-bold">{clk.count} clicks</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
